// src/components/Teacher/VisualResultEntry.js
// Teacher fills results directly ON the real Nigerian result sheet.
// Branding fetched from /api/school/branding — works for any role (admin/teacher).
// Student fields: read-only if already populated from DB; editable if blank.

import React, { useState, useEffect, useCallback } from 'react';
import { Save, Send, AlertCircle, X } from 'lucide-react';
import axios from 'axios';

const { REACT_APP_API_URL } = process.env;

// ─── Grade helpers ────────────────────────────────────────────────────────────
const getNigerianGrade = (total) => {
  if (total >= 75) return { grade: 'A1', remark: 'EXCELLENT' };
  if (total >= 70) return { grade: 'B2', remark: 'VERY GOOD' };
  if (total >= 65) return { grade: 'B3', remark: 'GOOD' };
  if (total >= 60) return { grade: 'C4', remark: 'CREDIT' };
  if (total >= 55) return { grade: 'C5', remark: 'CREDIT' };
  if (total >= 50) return { grade: 'C6', remark: 'CREDIT' };
  if (total >= 45) return { grade: 'D7', remark: 'PASS' };
  if (total >= 40) return { grade: 'E8', remark: 'PASS' };
  return            { grade: 'F9', remark: 'FAIL' };
};

// ─── Shared input style helpers ───────────────────────────────────────────────
const BASE_FONT = '"Times New Roman", Times, serif';

const inputStyle = (width = '100%', align = 'left', extra = {}) => ({
  width, border: 'none', borderBottom: '1px solid #aaa',
  background: 'transparent', outline: 'none',
  fontFamily: BASE_FONT, fontSize: '10px', color: '#000',
  padding: '0 1px', textAlign: align, ...extra,
});

const numInput = (extra = {}) => ({
  ...inputStyle('100%', 'center'),
  MozAppearance: 'textfield', ...extra,
});

const EDITABLE_BG   = { backgroundColor: '#fffde7' }; // yellow = teacher edits this
const READONLY_BG   = { backgroundColor: 'transparent' };
const COMPUTED_BG   = { backgroundColor: '#f0f0f0' };  // grey = auto-calculated

// ─── 16 trait definitions ─────────────────────────────────────────────────────
const AFFECTIVE_SKILLS = [
  { label: 'Attentiveness',            key: 'attentiveness' },
  { label: 'Honesty',                  key: 'honesty' },
  { label: 'Neatness',                 key: 'neatness' },
  { label: 'Politeness',               key: 'politeness' },
  { label: 'Punctuality/Assembly',     key: 'punctuality' },
  { label: 'Self Control/Calmness',    key: 'selfControl' },
  { label: 'Obedience',                key: 'obedience' },
  { label: 'Reliability',              key: 'reliability' },
  { label: 'Sense of Responsibility',  key: 'responsibility' },
  { label: 'Relationship With Others', key: 'relationship' },
];
const PSYCHOMOTOR_SKILLS = [
  { label: 'Handling Of Tools', key: 'handlingOfTools' },
  { label: 'Drawing/Painting',  key: 'drawingPainting' },
  { label: 'Handwriting',       key: 'handwriting' },
  { label: 'Public Speaking',   key: 'publicSpeaking' },
  { label: 'Speech Fluency',    key: 'speechFluency' },
  { label: 'Sports & Games',    key: 'sportsGames' },
];
const ALL_TRAITS = [...AFFECTIVE_SKILLS, ...PSYCHOMOTOR_SKILLS];

// ─── Main Component ───────────────────────────────────────────────────────────
const VisualResultEntry = ({
  student,
  template,
  existingResult = null,
  term,
  session,
  token,
  onClose,
  onSuccess,
}) => {
  // ── School branding ──────────────────────────────────────────────────────────
  const [school,   setSchool]   = useState({ name:'', address:'', phone:'', email:'', motto:'', logoBase64:'', principalName:'' });
  const [fetching, setFetching] = useState(true);

  // ── Editable student extras (teacher fills if blank in DB) ───────────────────
  const [extras, setExtras] = useState({ gender:'', dob:'', club:'' });

  // ── Result data ──────────────────────────────────────────────────────────────
  const [subjects,   setSubjects]   = useState([]);
  const [traits,     setTraits]     = useState({});
  const [attendance, setAttendance] = useState({ opened:'', present:'', absent:'' });
  const [comments,   setComments]   = useState({ teacher:'', principal:'' });
  const [termBegins, setTermBegins] = useState('');
  const [termEnds,   setTermEnds]   = useState('');
  const [nextTerm,   setNextTerm]   = useState('');
  const [classSize,  setClassSize]  = useState('');

  // ── Editable names on result sheet ─────────────────────────────────────────
  const [teacherName,    setTeacherName]    = useState('');
  const [principalName,  setPrincipalName]  = useState('');

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const authHdr = useCallback(
    () => (!token ? '' : token.startsWith('Bearer ') ? token : `Bearer ${token}`),
    [token]
  );

  // ── 1. Fetch branding from /api/school/branding (works for teachers too) ─────
  useEffect(() => {
    if (!token) { setError('Authentication token missing.'); setFetching(false); return; }
    axios.get(`${REACT_APP_API_URL}/api/teacher/school-branding`, {
      headers: { Authorization: authHdr() },
    })
    .then(res => {
      const s = res.data.school || {};
      setSchool({
        name:          s.name          || '',
        address:       s.address       || '',
        phone:         s.phone         || '',
        email:         s.email         || '',
        motto:         s.motto         || '',
        logoBase64:    s.logoBase64    || '',
        principalName: s.principalName || '',
      });
      // Pre-fill principal name from school branding (teacher can override)
      setPrincipalName(prev => prev || s.principalName || '');
      // Pre-fill term dates from school's active term settings.
      // These are stored as plain strings (e.g. "2024-09-09") — use directly.
      const toDateStr = v => {
        if (!v) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already YYYY-MM-DD
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      };
      setTermBegins(prev => prev || toDateStr(s.currentTermBegins));
      setTermEnds(prev   => prev || toDateStr(s.currentTermEnds));
      setNextTerm(prev   => prev || toDateStr(s.currentNextTermBegins));
    })
    .catch(err => console.error('[VisualResultEntry] Branding fetch failed:', err.message))
    .finally(() => setFetching(false));
  }, [token, authHdr]);

  // ── 2. Initialise from existingResult or template ────────────────────────────
  const defaultTraits = useCallback(() => {
    const t = {};
    ALL_TRAITS.forEach(s => { t[s.key] = 0; });
    return t;
  }, []);

  const emptySubjRow = (name = '') => ({
    subject: name, ca: '', exam: '',
    term1Total: '', term2Total: '', sessionAverage: '', classPosition: '', classAverage: '',
  });

  const buildEmptySubjects = useCallback(() => {
    const flat = template?.components?.subjects;
    if (Array.isArray(flat) && flat.length)
      return flat.map(s => emptySubjRow(typeof s === 'string' ? s : (s.name || s.subject || '')));
    const legacy = template?.components?.scoresTable?.subjects;
    if (Array.isArray(legacy) && legacy.length)
      return legacy.map(s => emptySubjRow(typeof s === 'string' ? s : (s.name || s.subject || '')));
    const n = template?.components?.scoresTable?.defaultSubjects || 12;
    return Array.from({ length: n }, emptySubjRow);
  }, [template]);

  useEffect(() => {
    // Pre-fill teacher-editable student extras from DB values
    setExtras({
      gender: student?.gender || '',
      dob:    student?.dob    || '',
      club:   student?.club   || '',
    });

    // Helper: fetch SubjectScore records for this student and merge into rows.
    // Marks rows that came from subject teachers with { fromSubjectTeacher: true }
    // so VisualResultEntry can show an indicator to the class teacher.
    const mergeSubjectScores = (baseRows, token, authHdr, setter) => {
      if (!token || !student?._id) { setter(baseRows); return; }
      axios.get(`${REACT_APP_API_URL}/api/teacher/subject-scores/for-student`, {
        headers: { Authorization: authHdr() },
        params:  { studentId: student._id, term, session },
      })
      .then(res => {
        const fresh = res.data.scores || [];
        if (!fresh.length) { setter(baseRows); return; }
        const freshMap = {};
        fresh.forEach(f => { freshMap[f.subject.toLowerCase().trim()] = f; });
        const filled = baseRows.map(row => {
          const match = freshMap[row.subject.toLowerCase().trim()];
          // Merge if: subject teacher has a score AND the row has no class-teacher data yet.
          // We consider a row "empty" if both ca and exam are exactly 0 (template default).
          // We do NOT overwrite if class teacher has already saved non-zero values.
          const rowIsEmpty = (row.ca === 0 || row.ca === '' || row.ca == null)
                          && (row.exam === 0 || row.exam === '' || row.exam == null);
          if (match && rowIsEmpty) {
            return {
              ...row,
              ca:   match.ca   || 0,
              exam: match.exam || 0,
              fromSubjectTeacher: true,
              subjectTeacherUpdatedAt: match.updatedAt || null,
            };
          }
          return row;
        });
        // Append subjects entered by subject teachers not present in template rows
        const templateNames = new Set(baseRows.map(r => r.subject.toLowerCase().trim()));
        fresh.forEach(f => {
          if (!templateNames.has(f.subject.toLowerCase().trim())) {
            filled.push({
              subject: f.subject,
              ca:   f.ca   || 0,
              exam: f.exam || 0,
              fromSubjectTeacher: true,
              subjectTeacherUpdatedAt: f.updatedAt || null,
            });
          }
        });
        setter(filled);
      })
      .catch(() => setter(baseRows)); // silently degrade — not critical
    };

    if (existingResult) {
      const s = (existingResult.subjects || []).map(sub => ({
        subject:        sub.subject || '',
        ca:             sub.ca   != null ? String(sub.ca)   : (sub.ca1 != null ? String(sub.ca1) : ''),
        exam:           sub.exam != null ? String(sub.exam) : '',
        term1Total:     sub.term1Total     != null ? String(sub.term1Total)     : '',
        term2Total:     sub.term2Total     != null ? String(sub.term2Total)     : '',
        sessionAverage: sub.sessionAverage != null ? String(sub.sessionAverage) : '',
        classPosition:  sub.classPosition  != null ? String(sub.classPosition)  : '',
        classAverage:   sub.classAverage   != null ? String(sub.classAverage)   : '',
      }));
      const baseRows = s.length ? s : buildEmptySubjects();
      mergeSubjectScores(baseRows, token, authHdr, setSubjects);
      setTraits(existingResult.affectiveTraits || defaultTraits());
      setAttendance(existingResult.attendance   || { opened:'', present:'', absent:'' });
      setComments(existingResult.comments       || { teacher:'', principal:'' });
      setTermBegins(existingResult.termBegins   ? new Date(existingResult.termBegins).toISOString().split('T')[0]   : '');
      setTermEnds(existingResult.termEnds       ? new Date(existingResult.termEnds).toISOString().split('T')[0]       : '');
      setNextTerm(existingResult.nextTermResumption ? new Date(existingResult.nextTermResumption).toISOString().split('T')[0] : '');
      setClassSize(existingResult.classSize     || '');
      if (existingResult.teacherName)   setTeacherName(existingResult.teacherName);
      if (existingResult.principalName) setPrincipalName(existingResult.principalName);
      if (existingResult.studentExtras) {
        setExtras(prev => ({
          gender: prev.gender || existingResult.studentExtras.gender || '',
          dob:    prev.dob    || existingResult.studentExtras.dob    || '',
          club:   prev.club   || existingResult.studentExtras.club   || '',
        }));
      }
      return;
    }

    // No existing result — build from template then merge SubjectScores
    const emptyRows = buildEmptySubjects();
    setTraits(defaultTraits());
    // Set term dates from template — use functional updater to not override
    // values already loaded from school branding (branding useEffect may run first)
    const toDateStr2 = v => {
      if (!v) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
      const d = new Date(v);
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
    };
    const tbFromTemplate = toDateStr2(template?.components?.termBegins);
    const teFromTemplate = toDateStr2(template?.components?.termEnds);
    const ntFromTemplate = toDateStr2(template?.components?.nextTermBegins);
    if (tbFromTemplate) setTermBegins(prev => prev || tbFromTemplate);
    if (teFromTemplate) setTermEnds(prev   => prev || teFromTemplate);
    if (ntFromTemplate) setNextTerm(prev   => prev || ntFromTemplate);
    if (template?.components?.classSize)      setClassSize(String(template.components.classSize));
    mergeSubjectScores(emptyRows, token, authHdr, setSubjects);
  }, [existingResult, template, student, buildEmptySubjects, defaultTraits]);

  // ── Subject helpers ───────────────────────────────────────────────────────────
  // Cross-term columns — any numeric field on a subject row
  const CROSS_TERM_FIELDS = new Set(['term1Total','term2Total','sessionAverage','classPosition','classAverage']);

  const updateSubject = (idx, field, raw) => {
    let val;
    if (field === 'subject') {
      val = raw;
    } else {
      // Strip non-digits; keep '' when empty so blank stays blank (not 0)
      val = String(raw).replace(/[^0-9.]/g, '');
    }
    setSubjects(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  };

  const blurSubject = (idx, field) => {
    const max = field === 'ca' ? 40 : 60;
    setSubjects(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      if (s[field] === '' || s[field] == null) return s; // leave blank
      const clamped = Math.min(max, Math.max(0, Number(s[field]) || 0));
      return { ...s, [field]: String(clamped) };
    }));
  };
  const addRow    = () => setSubjects(prev => [...prev, emptySubjRow()]);
  const removeRow = (idx) => setSubjects(prev => prev.filter((_, i) => i !== idx));

  // ── Trait click ───────────────────────────────────────────────────────────────
  const setTrait = (key, n) =>
    setTraits(prev => ({ ...prev, [key]: prev[key] === n ? 0 : n }));

  // ── Computed ──────────────────────────────────────────────────────────────────
  const computed      = subjects.map(s => {
    const total = Math.min(100, (Number(s.ca)||0) + (Number(s.exam)||0));
    return { ...s, total, ...getNigerianGrade(total) };
  });
  // validRows: subject name must be non-empty AND at least one score entered
  // This prevents saving zero-filled rows for subjects the teacher didn't touch
  const validRows     = computed.filter(s => s.subject?.trim() && (s.ca !== '' || s.exam !== ''));
  const totalObtain   = validRows.length * 100;
  const totalObtained = validRows.reduce((a, s) => a + s.total, 0);
  const pct           = totalObtain > 0 ? ((totalObtained / totalObtain) * 100).toFixed(1) : '0.0';
  const overallGrade  = getNigerianGrade(parseFloat(pct));

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async (submitToAdmin = false) => {
    setError('');
    if (!token)        { setError('Authentication token missing.'); return; }
    if (!validRows.length) { setError('Please enter at least one subject name.'); return; }

    const payload = {
      studentId:          student._id,
      term, session,
      subjects:           validRows.map(({ subject, ca, exam, term1Total, term2Total, sessionAverage, classPosition, classAverage }) => ({
        subject,
        ca:             Number(ca)   || 0,
        exam:           Number(exam) || 0,
        term1Total:     term1Total     !== '' && term1Total     != null ? Number(term1Total)     : undefined,
        term2Total:     term2Total     !== '' && term2Total     != null ? Number(term2Total)     : undefined,
        sessionAverage: sessionAverage !== '' && sessionAverage != null ? Number(sessionAverage) : undefined,
        classPosition:  classPosition  !== '' && classPosition  != null ? Number(classPosition)  : undefined,
        classAverage:   classAverage   !== '' && classAverage   != null ? Number(classAverage)   : undefined,
      })),
      affectiveTraits:    traits,
      attendance:         { opened: Number(attendance.opened)||0, present: Number(attendance.present)||0, absent: Number(attendance.absent)||0 },
      comments,
      studentExtras:      { gender: extras.gender||undefined, dob: extras.dob||undefined, club: extras.club||undefined },
      termBegins:         termBegins || undefined,
      termEnds:           termEnds   || undefined,
      nextTermResumption: nextTerm   || undefined,
      classSize:          classSize  ? Number(classSize) : undefined,
      teacherName:        teacherName    || undefined,
      principalName:      principalName  || undefined,
      status:             submitToAdmin ? 'submitted' : 'draft',
    };
    if (existingResult) payload.resultId = existingResult._id;

    try {
      setLoading(true);
      const res = await axios.post(
        `${REACT_APP_API_URL}/api/teacher/results`, payload,
        { headers: { Authorization: authHdr(), 'Content-Type': 'application/json' } }
      );
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Redirecting to login...');
        setTimeout(() => { window.location.href = '/login'; }, 2500);
      } else {
        const msg = err.response?.data?.message || err.response?.data?.error || (err.response?.data ? JSON.stringify(err.response.data) : null) || err.message || 'Failed to save result.';
        setError(msg);
        console.error('[SaveResult] Full error:', err.response?.data || err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!template) {
    return (
      <div className="alert alert-warning m-3 d-flex align-items-center gap-2">
        <AlertCircle size={20} />
        No template found for {term}, {session}. Ask your admin to create one.
      </div>
    );
  }

  // ─── Sheet styles ─────────────────────────────────────────────────────────────
  const S = {
    page: { fontFamily: BASE_FONT, fontSize:'11px', color:'#000', backgroundColor:'#fff', maxWidth:'900px', margin:'0 auto', padding:'12px 16px', border:'2px solid #000', boxSizing:'border-box' },
    hdr: { display:'flex', alignItems:'center', justifyContent:'center', gap:'14px', marginBottom:'6px', paddingBottom:'6px', borderBottom:'2px solid #000' },
    banner: { textAlign:'center', fontWeight:'bold', fontSize:'13px', border:'1px solid #000', padding:'3px 0', margin:'5px 0', backgroundColor:'#f0f0f0', textTransform:'uppercase' },
    topBand: { display:'grid', gridTemplateColumns:'2fr 1fr 1fr', border:'1px solid #000', marginBottom:'4px' },
    cell: { padding:'3px 5px', borderRight:'1px solid #000' },
    cellLast: { padding:'3px 5px' },
    secTitle: { fontWeight:'bold', textAlign:'center', borderBottom:'1px solid #ccc', marginBottom:'3px', paddingBottom:'2px', fontSize:'10px' },
    bioPair: { display:'flex', marginBottom:'2px', gap:'4px', alignItems:'center' },
    bioLbl: { fontWeight:'bold', fontSize:'10px', whiteSpace:'nowrap' },
    attGrid: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', textAlign:'center', marginTop:'2px' },
    attCol: { borderRight:'1px solid #ccc', padding:'1px 0' },
    attLbl: { fontSize:'9px', borderBottom:'1px solid #ccc' },
    sumGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1px', fontSize:'10px' },
    table: { width:'100%', borderCollapse:'collapse', marginBottom:'4px', fontSize:'10px' },
    th: { border:'1px solid #000', padding:'2px 3px', textAlign:'center', backgroundColor:'#e8e8e8', fontWeight:'bold', fontSize:'9px' },
    td: { border:'1px solid #000', padding:'1px 2px', textAlign:'center' },
    tdL: { border:'1px solid #000', padding:'1px 4px', textAlign:'left' },
    botGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px', marginBottom:'4px' },
    domBox: { border:'1px solid #000' },
    domHdr: { backgroundColor:'#e8e8e8', fontWeight:'bold', textAlign:'center', padding:'2px', fontSize:'10px', borderBottom:'1px solid #000' },
    domScaleHdr: { display:'flex', backgroundColor:'#e8e8e8', borderBottom:'1px solid #000', fontSize:'9px' },
    domScaleLbl: { flex:3, padding:'1px 4px', borderRight:'1px solid #000' },
    domScaleCols: { flex:2, display:'flex' },
    domScaleCol: { flex:1, textAlign:'center', borderRight:'1px solid #ccc', padding:'1px 0', fontWeight:'bold' },
    domRow: { display:'flex', borderBottom:'1px solid #e0e0e0', fontSize:'9.5px' },
    domRowLbl: { flex:3, padding:'1px 4px', borderRight:'1px solid #000' },
    domRowCols: { flex:2, display:'flex' },
    domRowCol: { flex:1, textAlign:'center', borderRight:'1px solid #ccc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:'bold', cursor:'pointer', userSelect:'none', minHeight:'14px' },
    cGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px', marginBottom:'4px' },
    cBox: { border:'1px solid #000', padding:'4px' },
    gradeBox: { border:'1px solid #000', padding:'4px', fontSize:'9.5px' },
    gradeHdr: { fontWeight:'bold', textAlign:'center', borderBottom:'1px solid #000', marginBottom:'3px', paddingBottom:'2px', fontSize:'10px' },
    gradeRow: { display:'flex', justifyContent:'space-between', marginBottom:'1px' },
    ratingBox: { border:'1px solid #000', padding:'4px', fontSize:'9px' },
  };

  const TraitRow = ({ skill, odd }) => {
    const val = traits[skill.key] || 0;
    return (
      <div style={{ ...S.domRow, backgroundColor: odd ? '#fafafa' : '#fff' }}>
        <div style={S.domRowLbl}>{skill.label}</div>
        <div style={S.domRowCols}>
          {[5,4,3,2,1].map(n => (
            <div key={n}
              style={{ ...S.domRowCol, backgroundColor: val===n ? '#1e293b' : 'transparent', color: val===n ? '#fff' : '#000' }}
              onClick={() => setTrait(skill.key, n)}
              title={`${skill.label} = ${n}`}
            >
              {val === n ? '✓' : ''}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DomainSection = ({ title, skills }) => (
    <div style={S.domBox}>
      <div style={S.domHdr}>{title}</div>
      <div style={S.domScaleHdr}>
        <div style={S.domScaleLbl}>Traits</div>
        <div style={S.domScaleCols}>
          {[5,4,3,2,1].map(n => <div key={n} style={S.domScaleCol}>{n}</div>)}
        </div>
      </div>
      {skills.map((sk, i) => <TraitRow key={sk.key} skill={sk} odd={i%2!==0} />)}
    </div>
  );

  const Btn = ({ onClick, disabled, bg, children }) => (
    <button onClick={onClick} disabled={disabled}
      style={{ padding:'6px 14px', backgroundColor:bg, color:'#fff', border:'none', borderRadius:'6px',
        cursor: disabled ? 'not-allowed':'pointer', fontWeight:'bold', fontSize:'13px',
        display:'flex', alignItems:'center', gap:'6px', opacity: disabled ? 0.6 : 1 }}>
      {children}
    </button>
  );

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.65)', zIndex:1050, overflowY:'auto', padding:'12px' }}>
      <div style={{ maxWidth:'960px', margin:'0 auto' }}>

        {/* Sticky toolbar */}
        <div style={{ position:'sticky', top:0, zIndex:20, backgroundColor:'#1e293b', color:'#fff',
          padding:'10px 16px', borderRadius:'8px 8px 0 0',
          display:'flex', justifyContent:'space-between', alignItems:'center',
          boxShadow:'0 2px 8px rgba(0,0,0,0.5)' }}>
          <div>
            <div style={{ fontWeight:'bold', fontSize:'15px' }}>{student?.name} — Result Entry</div>
            <div style={{ fontSize:'12px', opacity:0.65 }}>{term}, {session} · Fill directly on the sheet below</div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <Btn onClick={() => handleSave(false)} disabled={loading||fetching} bg="#3b82f6">
              <Save size={15}/>{loading ? 'Saving…':'Save Draft'}
            </Btn>
            <Btn onClick={() => handleSave(true)} disabled={loading||fetching} bg="#16a34a">
              <Send size={15}/>{loading ? 'Submitting…':'Submit to Admin'}
            </Btn>
            <Btn onClick={onClose} disabled={loading} bg="rgba(255,255,255,0.15)">
              <X size={16}/>
            </Btn>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ backgroundColor:'#fee2e2', color:'#991b1b', padding:'8px 14px',
            borderLeft:'4px solid #dc2626', fontSize:'13px', display:'flex', gap:'8px', alignItems:'center' }}>
            <AlertCircle size={16}/> {error}
          </div>
        )}

        {/* Tip strip */}
        <div style={{ backgroundColor:'#fffbeb', borderLeft:'4px solid #f59e0b', padding:'6px 14px', fontSize:'12px', color:'#92400e' }}>
          💡 <strong>Yellow</strong> = editable. <strong style={{ color:'#1e40af' }}>Blue (✓ ST)</strong> = pre-filled by subject teacher, still editable. Click 5–1 to rate traits.
          {fetching && <span style={{ marginLeft:'10px', color:'#555' }}>⏳ Loading school branding…</span>}
        </div>

        {/* ═══ RESULT SHEET ═══ */}
        <div style={S.page}>

          {/* HEADER */}
          <div style={S.hdr}>
            {school.logoBase64 ? (
              <img src={school.logoBase64} alt="School Logo"
                style={{ width:72, height:72, objectFit:'contain', flexShrink:0 }} />
            ) : (
              <div style={{ width:72, height:72, border:'1px dashed #bbb', flexShrink:0,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'9px', color:'#aaa', textAlign:'center', lineHeight:'1.3' }}>
                {fetching ? '…' : 'No Logo\n(Settings →\nBranding)'}
              </div>
            )}

            <div style={{ flex:1, textAlign:'center' }}>
              {school.name ? (
                <div style={{ fontSize:'22px', fontWeight:'bold', textTransform:'uppercase', letterSpacing:'1px' }}>
                  {school.name}
                </div>
              ) : (
                <div style={{ fontSize:'13px', color:'#999', fontStyle:'italic' }}>
                  {fetching ? 'Loading…' : 'School name not set — go to Settings → Branding'}
                </div>
              )}
              {school.motto   && <div style={{ fontSize:'11px', fontStyle:'italic' }}>Motto: {school.motto}</div>}
              {school.address && <div style={{ fontSize:'9.5px' }}>{school.address}</div>}
              {(school.phone||school.email) && (
                <div style={{ fontSize:'9.5px' }}>
                  {school.phone && `Tel: ${school.phone}`}
                  {school.phone && school.email && ' | '}
                  {school.email && `Email: ${school.email}`}
                </div>
              )}
            </div>


          </div>

          {/* SESSION BANNER */}
          <div style={S.banner}>
            {session} — {(term||'').toUpperCase()} PUPIL'S PERFORMANCE REPORT
          </div>

          {/* TOP BAND */}
          <div style={S.topBand}>

            {/* PERSONAL DATA */}
            <div style={S.cell}>
              <div style={S.secTitle}>PERSONAL DATA</div>

              {/* NAME — always read-only */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>NAME:</span>
                <span style={{ fontSize:'10px' }}>{student?.name || '—'}</span>
              </div>

              {/* ADMIN NO — read-only */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>ADMIN NO:</span>
                <span style={{ fontSize:'10px' }}>{student?.admNo || student?.regNo || '—'}</span>
              </div>

              {/* GENDER — editable if not in DB */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>GENDER:</span>
                {student?.gender ? (
                  <span style={{ fontSize:'10px' }}>{student.gender}</span>
                ) : (
                  <select
                    value={extras.gender}
                    onChange={e => setExtras(p => ({ ...p, gender: e.target.value }))}
                    style={{ ...inputStyle('auto'), ...EDITABLE_BG, fontSize:'10px' }}
                  >
                    <option value="">— select —</option>
                    <option>Male</option><option>Female</option>
                  </select>
                )}
              </div>

              {/* CLASS — read-only */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>CLASS:</span>
                <span style={{ fontSize:'10px' }}>{student?.classId?.name || student?.className || '—'}</span>
              </div>

              {/* DOB — editable if not in DB */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>D.O.B:</span>
                {student?.dob ? (
                  <span style={{ fontSize:'10px' }}>{student.dob}</span>
                ) : (
                  <input type="date"
                    value={extras.dob}
                    onChange={e => setExtras(p => ({ ...p, dob: e.target.value }))}
                    style={{ ...inputStyle('auto'), ...EDITABLE_BG, fontSize:'9px' }}
                  />
                )}
              </div>

              {/* CLUB — editable if not in DB */}
              <div style={S.bioPair}>
                <span style={S.bioLbl}>CLUB/SOCIETY:</span>
                {student?.club ? (
                  <span style={{ fontSize:'10px' }}>{student.club}</span>
                ) : (
                  <input type="text"
                    value={extras.club}
                    onChange={e => setExtras(p => ({ ...p, club: e.target.value }))}
                    placeholder="e.g. Science Club"
                    style={{ ...inputStyle('100%'), ...EDITABLE_BG }}
                  />
                )}
              </div>
            </div>

            {/* ATTENDANCE + TERM DATES */}
            <div style={S.cell}>
              <div style={S.secTitle}>ATTENDANCE</div>
              <div style={S.attGrid}>
                {[['No of Times\nSchool Opened','opened'],['No of Times\nPresent','present'],['No of Times\nAbsent','absent']].map(([lbl,key],i) => (
                  <div key={key} style={{ ...S.attCol, borderRight: i<2 ? '1px solid #ccc' : 'none' }}>
                    <div style={S.attLbl}>{lbl}</div>
                    <input type="number" min={0}
                      value={attendance[key]}
                      onChange={e => setAttendance(p => ({ ...p, [key]: e.target.value }))}
                      style={{ ...numInput(), fontSize:'13px', fontWeight:'bold', ...EDITABLE_BG }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ marginTop:'6px', fontWeight:'bold', textAlign:'center', borderTop:'1px solid #ccc', paddingTop:'3px', fontSize:'10px' }}>
                TERMINAL DURATION
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'9.5px', marginTop:'2px', gap:'4px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'bold' }}>Term Beginning</div>
                  <input type="date" value={termBegins} onChange={e => setTermBegins(e.target.value)}
                    style={{ ...inputStyle('100%'), ...EDITABLE_BG, fontSize:'9px' }} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'bold' }}>Term Ending</div>
                  <input type="date" value={termEnds} onChange={e => setTermEnds(e.target.value)}
                    style={{ ...inputStyle('100%'), ...EDITABLE_BG, fontSize:'9px' }} />
                </div>
              </div>
            </div>

            {/* PERFORMANCE SUMMARY */}
            <div style={S.cellLast}>
              <div style={S.secTitle}>PERFORMANCE SUMMARY</div>
              <div style={S.sumGrid}>
                {[
                  ['Total Score Obtainable', totalObtain],
                  ['Total Score Obtained',   totalObtained],
                  ['%TAGE',                  `${pct}%`],
                  ['GRADE',                  overallGrade.grade],
                  ['POSITION',               existingResult?.overallPosition ? `${existingResult.overallPosition} of ${existingResult.classSize || classSize || '?'}` : '—'],
                ].map(([lbl, val]) => (
                  <React.Fragment key={lbl}>
                    <div style={{ fontWeight:'bold', fontSize:'10px' }}>{lbl}</div>
                    <div style={{ fontWeight: lbl==='GRADE' ? 'bold':'normal', fontSize:'10px' }}>{val}</div>
                  </React.Fragment>
                ))}
                <div style={{ fontWeight:'bold', fontSize:'10px' }}>CLASS SIZE</div>
                <div>
                  <input type="number" min={1}
                    value={classSize}
                    onChange={e => setClassSize(e.target.value)}
                    style={{ ...numInput(), ...EDITABLE_BG, width:'50px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* COGNITIVE DOMAIN TABLE */}
          <div style={{ fontWeight:'bold', textAlign:'center', backgroundColor:'#d8d8d8', border:'1px solid #000', borderBottom:'none', padding:'2px', fontSize:'11px' }}>
            COGNITIVE DOMAIN
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width:'20%', textAlign:'left' }} rowSpan={2}>SUBJECTS</th>
                {['C.A.','EXAM','TOTAL','2ND TERM','1ST TERM','SESSION AVG','CLASS POS.','CLASS AVG','GRADE','REMARKS',''].map((h,i) => (
                  <th key={i} style={{ ...S.th, width: i===10 ? '22px':undefined }}>{h}</th>
                ))}
              </tr>
              <tr>
                {['40','60','100','100','100','100','','','','',''].map((v,i) => (
                  <th key={i} style={S.th}>{v}</th>
                ))}
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {computed.map((subj, idx) => (
                <tr key={idx} style={{ backgroundColor: idx%2===0 ? '#fff':'#f9f9f9' }}>
                  <td style={S.tdL}>
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <input type="text" value={subj.subject}
                        onChange={e => updateSubject(idx, 'subject', e.target.value)}
                        style={{ ...inputStyle('100%','left'), ...EDITABLE_BG, textTransform:'uppercase' }}
                        placeholder="Subject name"
                      />
                      {subj.fromSubjectTeacher && (
                        <span title={`Score entered by subject teacher${subj.subjectTeacherUpdatedAt ? ' on ' + new Date(subj.subjectTeacherUpdatedAt).toLocaleDateString('en-GB') : ''}`}
                          style={{ fontSize:'8px', background:'#dbeafe', color:'#1e40af', borderRadius:'3px',
                            padding:'1px 4px', whiteSpace:'nowrap', fontWeight:600, cursor:'default', flexShrink:0 }}>
                          ✓ ST
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...S.td, background: subj.fromSubjectTeacher ? '#eff6ff' : undefined }}>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                      value={subj.ca}
                      placeholder="—"
                      onChange={e => updateSubject(idx, 'ca', e.target.value)}
                      onBlur={() => blurSubject(idx, 'ca')}
                      style={{ ...numInput(), ...(subj.fromSubjectTeacher ? { backgroundColor:'#eff6ff' } : EDITABLE_BG), width:'38px' }}
                    />
                  </td>
                  <td style={{ ...S.td, background: subj.fromSubjectTeacher ? '#eff6ff' : undefined }}>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                      value={subj.exam}
                      placeholder="—"
                      onChange={e => updateSubject(idx, 'exam', e.target.value)}
                      onBlur={() => blurSubject(idx, 'exam')}
                      style={{ ...numInput(), ...(subj.fromSubjectTeacher ? { backgroundColor:'#eff6ff' } : EDITABLE_BG), width:'38px' }}
                    />
                  </td>
                  <td style={{ ...S.td, fontWeight:'bold', ...COMPUTED_BG }}>{(subj.ca !== '' || subj.exam !== '') ? subj.total : ''}</td>
                  {[
                    ['term2Total','100'],['term1Total','100'],
                    ['sessionAverage','100'],['classPosition',''],['classAverage','100']
                  ].map(([field], i) => (
                    <td key={i} style={S.td}>
                      <input
                        type="text" inputMode="numeric" pattern="[0-9]*"
                        value={subj[field] ?? ''}
                        placeholder="—"
                        onChange={e => updateSubject(idx, field, e.target.value)}
                        style={{ ...numInput(), ...EDITABLE_BG, width:'34px', fontSize:'9px' }}
                      />
                    </td>
                  ))}
                  <td style={{ ...S.td, fontWeight:'bold' }}>{subj.subject?.trim() ? subj.grade : ''}</td>
                  <td style={{ ...S.td, fontSize:'8.5px' }}>{subj.subject?.trim() ? subj.remark : ''}</td>
                  <td style={{ ...S.td, padding:0 }}>
                    <button type="button" onClick={() => removeRow(idx)}
                      style={{ border:'none', background:'none', cursor:'pointer', color:'#dc2626', padding:'2px 4px', fontSize:'12px' }}>×</button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={12} style={{ ...S.td, padding:'3px', textAlign:'center' }}>
                  <button type="button" onClick={addRow}
                    style={{ border:'1px dashed #999', background:'#fafafa', cursor:'pointer', fontSize:'11px', padding:'2px 14px', borderRadius:'3px', color:'#555' }}>
                    + Add Subject
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          {/* AFFECTIVE / PSYCHOMOTOR + GRADE SCALE */}
          <div style={S.botGrid}>
            <DomainSection title="AFFECTIVE DOMAIN"    skills={AFFECTIVE_SKILLS} />
            <div>
              <DomainSection title="PSYCHOMOTOR DOMAIN" skills={PSYCHOMOTOR_SKILLS} />
              <div style={{ ...S.gradeBox, marginTop:'4px' }}>
                <div style={S.gradeHdr}>Grade Scale</div>
                {[['A1','75–100','EXCELLENT'],['B2','70–74','VERY GOOD'],
                  ['B3','65–69','GOOD'],['C4','60–64','CREDIT'],
                  ['C5','55–59','CREDIT'],['C6','50–54','CREDIT'],
                  ['D7','45–49','PASS'],['E8','40–44','PASS'],
                  ['F9','0–39','FAIL'],
                ].map(([g,r,d],i) => (
                  <div key={i} style={{ ...S.gradeRow, backgroundColor: i%2===0?'#fff':'#f5f5f5', padding:'1px 0' }}>
                    <span style={{ fontWeight:'bold', width:'22px', display:'inline-block' }}>{g}</span>
                    <span style={{ width:'72px', display:'inline-block' }}>{r}</span>
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* COMMENTS — both editable by teacher; admin can update principal comment later */}
          <div style={S.cGrid}>
            <div style={S.cBox}>
              <div style={{ fontWeight:'bold', fontSize:'10px', marginBottom:'2px' }}>Class Teacher's Remark</div>
              <textarea
                value={comments.teacher}
                onChange={e => setComments(p => ({ ...p, teacher: e.target.value }))}
                rows={3}
                placeholder="Enter your remark about this student's performance…"
                style={{ width:'100%', border:'none', borderBottom:'1px solid #aaa', resize:'vertical',
                  fontFamily: BASE_FONT, fontSize:'10px', fontStyle:'italic', outline:'none', ...EDITABLE_BG, padding:'2px' }}
              />
              <div style={{ borderTop:'1px solid #000', marginTop:'6px', paddingTop:'2px', fontSize:'9.5px', display:'flex', alignItems:'center', gap:'4px' }}>
                <strong>Name:</strong>
                <input
                  type="text"
                  value={teacherName}
                  onChange={e => setTeacherName(e.target.value)}
                  placeholder="Teacher's name"
                  style={{ ...inputStyle('100%'), ...EDITABLE_BG, fontSize:'9.5px' }}
                />
              </div>
            </div>
            <div style={S.cBox}>
              <div style={{ fontWeight:'bold', fontSize:'10px', marginBottom:'2px' }}>Head Teacher's Remark</div>
              <textarea
                value={comments.principal}
                onChange={e => setComments(p => ({ ...p, principal: e.target.value }))}
                rows={3}
                placeholder="(Optional — admin can also update before sending to parent)"
                style={{ width:'100%', border:'none', borderBottom:'1px solid #aaa', resize:'vertical',
                  fontFamily: BASE_FONT, fontSize:'10px', fontStyle:'italic', outline:'none', ...EDITABLE_BG, padding:'2px' }}
              />
              <div style={{ borderTop:'1px solid #000', marginTop:'6px', paddingTop:'2px', fontSize:'9.5px', display:'flex', alignItems:'center', gap:'4px' }}>
                <strong>Name:</strong>
                <input
                  type="text"
                  value={principalName}
                  onChange={e => setPrincipalName(e.target.value)}
                  placeholder={school.principalName || "Head teacher's name"}
                  style={{ ...inputStyle('100%'), ...EDITABLE_BG, fontSize:'9.5px' }}
                />
              </div>
            </div>
          </div>

          {/* STATUS + NEXT TERM */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', borderTop:'1px solid #000', paddingTop:'5px', marginTop:'4px' }}>
            <div>
              <div style={{ fontSize:'11px', marginBottom:'4px' }}>
                <strong>Status:</strong>{' '}
                <span style={{ fontWeight:'bold', textDecoration:'underline', fontSize:'12px' }}>
                  {parseFloat(pct) >= 40 ? 'PROMOTED' : 'REPEATED'}
                </span>
              </div>
              <div style={{ fontSize:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
                <strong>Next Term Begins:</strong>
                <input type="date" value={nextTerm} onChange={e => setNextTerm(e.target.value)}
                  style={{ ...inputStyle('auto'), ...EDITABLE_BG, fontSize:'10px' }} />
              </div>
            </div>
            <div style={S.ratingBox}>
              <div style={{ fontWeight:'bold', fontSize:'10px', marginBottom:'3px' }}>Rating Indices</div>
              <div>5 - Maintains an Excellent degree of</div>
              <div>&nbsp;&nbsp;&nbsp;Observable (Obs) traits</div>
              <div>4 - Maintains a High level of Obs traits</div>
              <div>3 - Acceptable level of Obs traits</div>
              <div>2 - Shows Minimal regard for Obs traits</div>
              <div>1 - Has No regard for Observable traits</div>
            </div>
          </div>

        </div>{/* end sheet */}

        {/* Bottom save bar */}
        <div style={{ backgroundColor:'#1e293b', padding:'10px 16px', borderRadius:'0 0 8px 8px',
          display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <Btn onClick={onClose} disabled={loading} bg="rgba(255,255,255,0.12)">Cancel</Btn>
          <Btn onClick={() => handleSave(false)} disabled={loading||fetching} bg="#3b82f6">
            <Save size={15}/>{loading ? 'Saving…':'Save Draft'}
          </Btn>
          <Btn onClick={() => handleSave(true)} disabled={loading||fetching} bg="#16a34a">
            <Send size={15}/>{loading ? 'Submitting…':'Submit to Admin'}
          </Btn>
        </div>

      </div>
    </div>
  );
};

export default VisualResultEntry;